import { UUID_PATTERN } from "~/api/reasoningRequestIds";

export interface ReasoningLaunch {
  scope?: string;
  source?: string;
  digest?: string;
  startLine?: number;
  endLine?: number;
}

export const REASONING_SCENARIOS = [
  {
    id: "source-correction",
    label: "Correct stale knowledge",
    query:
      "Compare the current sources with earlier conclusions. Identify stale claims, proposed corrections and which conclusions need reevaluation.",
    toward: "Keep conclusions aligned with current evidence",
    signal: "Affected conclusions reevaluated against corrected sources",
  },
  {
    id: "conflicting-contributions",
    label: "Balance conflicting advice",
    query:
      "Compare the roles' advice, cite supporting and conflicting evidence, and propose a decision that satisfies the hard criteria. Preserve unresolved disagreements.",
    toward: "Make an evidence-grounded decision across roles",
    signal: "Decision reviewed by affected roles with disagreements recorded",
  },
  {
    id: "causal-attention",
    label: "Investigate causes and priorities",
    query:
      "Inspect declared prerequisites before measured associations. Compare alternative explanations, identify missing evidence and propose the next verification step without claiming causal proof.",
    toward: "Direct attention toward useful verification",
    signal: "The proposed verification produces a retained observation",
  },
  {
    id: "retry-and-recovery",
    label: "Plan recovery and follow-up",
    query:
      "Review interruption, replay, permission changes and budget exhaustion. Propose recovery steps, explicit stop conditions and measurable follow-up without assuming actions already happened.",
    toward: "Recover without duplicate effects or unsupported claims",
    signal: "Recovery is observed and a postmortem is submitted",
  },
] as const;

/** Navigation supplies a source reference, never source text or authority. The API verifies the digest. */
export function parseReasoningLaunch(search: Record<string, unknown>): ReasoningLaunch {
  const scope = typeof search.scope === "string" ? search.scope.slice(0, 100) : undefined;
  if (search.source === undefined) return { scope };
  if (
    typeof search.source !== "string" ||
    !UUID_PATTERN.test(search.source) ||
    typeof search.digest !== "string" ||
    !/^[0-9a-f]{64}$/.test(search.digest)
  )
    throw new Error("The reasoning source link is incomplete. Reopen the selection from VS Code.");
  const startLine = Number(search.startLine);
  const endLine = Number(search.endLine);
  if (
    !Number.isSafeInteger(startLine) ||
    startLine < 1 ||
    !Number.isSafeInteger(endLine) ||
    endLine < startLine
  )
    throw new Error("The reasoning selection has an invalid line range.");
  return { scope, source: search.source, digest: search.digest, startLine, endLine };
}
